import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { Receipt, TrendingUp, TrendingDown, Wallet, FileDown, FileSpreadsheet, Plus, Trash2, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const COLORS = ["#F97316", "#3B82F6", "#10B981", "#8B5CF6", "#EF4444", "#F59E0B"];

export default function Accounting() {
  const { company } = useAuth();
  const currency = company?.currency || "FCFA";
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rep, ex] = await Promise.all([
        api.get(`/accounting?period=${period}`),
        api.get("/expenses"),
      ]);
      setData(rep.data);
      setExpenses(ex.data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement";
      setError({ status: e.response?.status, msg });
      if (e.response?.status !== 403) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [period]);

  const download = (fmt) => {
    window.open(`${API_BASE}/accounting/export.${fmt}?period=${period}`, "_blank");
  };

  if (loading && !data) return <div className="text-slate-500">Chargement…</div>;
  if (error && !data) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-4" data-testid="accounting-error-state">
        <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-500/20 text-red-500 grid place-items-center mx-auto">
          <Receipt className="w-8 h-8" />
        </div>
        <div className="font-display font-bold text-xl">
          {error.status === 403 ? "Accès refusé" : "Une erreur est survenue"}
        </div>
        <p className="text-sm text-slate-500">
          {error.status === 403
            ? "Le module Comptabilité est réservé aux Créateurs et Admins."
            : error.msg}
        </p>
      </div>
    );
  }

  const kpis = [
    { label: "Chiffre d'affaires", value: data.gross_revenue, icon: TrendingUp, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-500/10" },
    { label: "TVA collectée", value: data.tax_collected, icon: Receipt, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-500/10" },
    { label: "Frais collectés", value: data.fees_collected, icon: ArrowUpRight, color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-500/10" },
    { label: "Remboursements", value: data.refund_amount, icon: ArrowDownRight, color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-500/10" },
    { label: "Revenus nets", value: data.net_revenue, icon: Wallet, color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-500/10" },
    { label: "Dépenses", value: data.total_expenses, icon: TrendingDown, color: "text-red-500", bg: "bg-red-50 dark:bg-red-500/10" },
    { label: "Bénéfice net", value: data.profit, icon: TrendingUp, color: data.profit >= 0 ? "text-emerald-500" : "text-red-500", bg: data.profit >= 0 ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-red-50 dark:bg-red-500/10" },
    { label: "Billets vendus", value: data.tickets_count, icon: Receipt, isCount: true, color: "text-slate-500", bg: "bg-slate-50 dark:bg-slate-800" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Comptabilité</h1>
          <p className="text-sm text-slate-500">Chiffre d'affaires, TVA, frais, remboursements et dépenses</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="accounting-period-tabs">
              <TabsTrigger value="today" data-testid="acc-period-today">Aujourd'hui</TabsTrigger>
              <TabsTrigger value="7d" data-testid="acc-period-7d">7j</TabsTrigger>
              <TabsTrigger value="30d" data-testid="acc-period-30d">30j</TabsTrigger>
              <TabsTrigger value="month" data-testid="acc-period-month">Mois</TabsTrigger>
              <TabsTrigger value="year" data-testid="acc-period-year">Année</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={() => download("pdf")} className="btn-pill" variant="outline" data-testid="export-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
          <Button onClick={() => download("xlsx")} className="btn-pill bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-xlsx-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="hover:shadow-md transition-shadow" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{k.label}</div>
                <div className={`w-8 h-8 rounded-lg grid place-items-center ${k.bg}`}>
                  <k.icon className={`w-4 h-4 ${k.color}`} />
                </div>
              </div>
              <div className="stat-num mt-2">
                {k.isCount ? k.value : formatCurrency(k.value, currency)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display text-lg">Évolution journalière</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.daily}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                <Legend />
                <Bar dataKey="revenue" name="Revenus" fill="#F97316" radius={[6, 6, 0, 0]} />
                <Bar dataKey="tax" name="TVA" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="refunds" name="Remboursements" fill="#EF4444" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Répartition paiements</CardTitle></CardHeader>
          <CardContent className="h-72">
            {data.by_payment_method.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.by_payment_method} dataKey="revenue" nameKey="method" innerRadius={50} outerRadius={90}>
                    {data.by_payment_method.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-slate-500 text-sm">Aucune donnée</div>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display text-lg">Dépenses</CardTitle>
          <NewExpenseDialog onCreated={load} />
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left">Catégorie</th>
                <th className="text-left">Libellé</th>
                <th className="text-left">Paiement</th>
                <th className="text-right">Montant</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                  <td className="p-3">{e.date}</td>
                  <td><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">{e.category}</span></td>
                  <td>{e.label}</td>
                  <td className="text-xs text-slate-500">{e.payment_method}</td>
                  <td className="text-right font-semibold text-red-600">-{formatCurrency(e.amount, currency)}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="icon" onClick={async () => {
                      if (!confirm("Supprimer cette dépense ?")) return;
                      try { await api.delete(`/expenses/${e.id}`); toast.success("Supprimé"); load(); }
                      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                    }} data-testid={`delete-expense-${e.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Aucune dépense enregistrée</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data.by_expense_category.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Dépenses par catégorie (période)</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.by_expense_category} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                <Bar dataKey="amount" fill="#EF4444" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const EXP_CATS = ["Carburant", "Salaires", "Entretien véhicule", "Loyer", "Fournitures", "Marketing", "Impôts", "Autre"];

function NewExpenseDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "Carburant",
    label: "",
    amount: "",
    payment_method: "Espèces",
    notes: "",
  });

  const save = async () => {
    if (!form.label || !form.amount) { toast.error("Libellé et montant requis"); return; }
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Dépense enregistrée");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), category: "Carburant", label: "", amount: "", payment_method: "Espèces", notes: "" });
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle dépense</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouvelle dépense</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="new-exp-date" /></div>
          <div>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="new-exp-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{EXP_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Libellé</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="new-exp-label" placeholder="Ex: Plein de gasoil bus n°3" /></div>
          <div><Label>Montant</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="new-exp-amount" placeholder="25000" /></div>
          <div>
            <Label>Paiement</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="new-exp-pay"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Espèces">Espèces</SelectItem>
                <SelectItem value="Wave Money">Wave Money</SelectItem>
                <SelectItem value="Virement">Virement</SelectItem>
                <SelectItem value="Chèque">Chèque</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Notes (optionnel)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="new-exp-notes" /></div>
        </div>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600 w-full mt-3" onClick={save} data-testid="save-expense-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}
